/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Dispatch,
  useCallback,
  useRef,
  useState,
} from 'react';
import useAsyncFn from 'react-use/esm/useAsyncFn';

type EntityStoreState = {
  requestedEntities: Set<string>;
  outstandingEntities: Map<string, Promise<Entity | undefined>>;
  cachedEntities: Map<string, Entity>;
};

/**
 * Lightweight local cache for catalog entities keyed by entity ref.
 */
export const useEntityStore = (): {
  entities: Record<string, Entity>;
  loading: boolean;
  error?: Error;
  requestEntities: Dispatch<string[]>;
} => {
  const catalogClient = useApi(catalogApiRef);
  const state = useRef<EntityStoreState>({
    requestedEntities: new Set<string>(),
    outstandingEntities: new Map(),
    cachedEntities: new Map(),
  });
  const [entities, setEntities] = useState<Record<string, Entity>>({});

  const updateEntities = useCallback(() => {
    const { cachedEntities, requestedEntities } = state.current;
    const filtered: Record<string, Entity> = {};
    requestedEntities.forEach(entityRef => {
      const entity = cachedEntities.get(entityRef);
      if (entity) {
        filtered[entityRef] = entity;
      }
    });
    setEntities(filtered);
  }, []);

  const [asyncState, fetch] = useAsyncFn(async () => {
    const { requestedEntities, cachedEntities } = state.current;
    const entityRefs = Array.from(requestedEntities).filter(
      entityRef => !cachedEntities.has(entityRef),
    );

    if (entityRefs.length === 0) {
      updateEntities();
      return;
    }

    const { items } = await catalogClient.getEntitiesByRefs({ entityRefs });
    items.forEach(item => {
      if (item) {
        const ref = stringifyEntityRef(item);
        cachedEntities.set(ref, item);
      }
    });

    updateEntities();
  }, [updateEntities, state]);

  const { loading, error } = asyncState;

  const requestEntities = useCallback(
    (entityRefs: string[]) => {
      const requested = new Set(entityRefs);
      const { requestedEntities } = state.current;

      const isDifferent =
        requested.size !== requestedEntities.size ||
        Array.from(requested).some(ref => !requestedEntities.has(ref));

      if (!isDifferent) {
        return;
      }

      state.current.requestedEntities = requested;
      fetch();
      updateEntities();
    },
    [fetch, updateEntities],
  );

  return {
    entities,
    loading,
    error,
    requestEntities,
  };
};

